import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel, GridPaginationModel } from '@mui/x-data-grid';

import { getRegions } from '@luxuryescapes/lib-regions';
import { ExtendedRegion } from '@luxuryescapes/lib-regions/lib/extended';

import useCurrentTenant from '~/hooks/useCurrentTenant';
import usePromoDisplayCheckResults, {
  GetPromoDisplayCheckResultsFilter,
  PromoDisplayConfigResult,
} from '~/hooks/usePromoDisplayCheckResults';
import useQuery from '~/hooks/useQuery';

import { PromoMeta } from '~/services/PromoService';

import dateFormatter from '~/utils/dateFormatter';
import deleteNulls from '~/utils/deleteNulls';

import CopyableField from '../Common/CopyableField';
import DebugModal from '../DebugModal/DebugModal';
import PromoLink from '../Promo/PromoLink';
import usePromoMeta from '../Promo/hooks/usePromoMeta';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  flex: 1,
};

export default function PromoDisplayCheckResults() {
  const getColumns = (
    resultCodes: PromoMeta['promo_display_config_result_code'],
  ): GridColDef<PromoDisplayConfigResult>[] => [
    {
      field: 'promoDisplayConfigId',
      headerName: 'Promo Display Config ID',
      renderCell: (params) => (
        <Link
          component="a"
          href={`/marketing/promo-display-config?page_num=0&page_size=100&source=ALL&medium=ALL&campaign=ALL&region=${params.row.region}&editingConfigId=${params.row.promoDisplayConfigId}`}
          target="_blank"
          rel="noreferrer"
        >
          {params.row.promoDisplayConfigId}
        </Link>
      ),
      ...sharedOpts,
    },
    {
      field: 'region',
      headerName: 'Region',
      ...sharedOpts,
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      renderCell: (params) => dateFormatter(params.row.createdAt),
      ...sharedOpts,
    },
    {
      field: 'resultCode',
      headerName: 'Result Code',
      renderCell: (params) => (
        <Tooltip title={resultCodes.find((resultCode) => resultCode.resultCode === params.row.resultCode)?.description}>
          <Typography>{params.row.resultCode}</Typography>
        </Tooltip>
      ),
      ...sharedOpts,
    },
    {
      field: 'displayOptionKey',
      headerName: 'Display Option Key',
      renderCell: (params) => <CopyableField value={params.row.displayOptionKey} />,
      ...sharedOpts,
    },
    {
      field: 'parentCodeName',
      headerName: 'Parent Code Name',
      renderCell: (params) => <PromoLink codeName={params.row.parentCodeName} />,
      ...sharedOpts,
    },
    {
      field: 'url',
      headerName: 'Url',
      renderCell: (params) => <>{params.row.siteDisplayPath ?? '--'}</>,
      ...sharedOpts,
    },
    {
      field: 'codeName',
      headerName: 'Code Name',
      renderCell: (params) => <PromoLink codeName={params.row.codeName} />,
      ...sharedOpts,
    },

    {
      field: 'details',
      headerName: 'Details',
      renderCell: (params) => (
        <Stack direction="column" spacing={1}>
          <Typography>{params.row.headerTextOverride}</Typography>
          <Typography variant="subtitle1">{params.row.bodyTextOverride}</Typography>
        </Stack>
      ),
      ...sharedOpts,
    },
    {
      field: 'Debug',
      renderCell: (params) => <DebugModal type="generic" data={params.row} />,
      ...sharedOpts,
    },
  ];

  const query = useQuery();
  const defaultValues: GetPromoDisplayCheckResultsFilter = {
    page: query.get('page') ? Number(query.get('page')) : 0,
    limit: query.get('limit') ? Number(query.get('limit')) : 10,
    promoDisplayConfigId: query.get('promoDisplayConfigId') || '',
    region: query.get('region') || '',
    resultCode: query.get('resultCode') || '',
    displayOptionKey: query.get('displayOptionKey') || '',
  };

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: defaultValues.page,
    pageSize: defaultValues.limit,
  });

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm({
    defaultValues: {
      promoDisplayConfigId: defaultValues.promoDisplayConfigId,
      region: defaultValues.region,
      resultCode: defaultValues.resultCode,
      displayOptionKey: defaultValues.displayOptionKey,
    },
  });

  const currentFilter: GetPromoDisplayCheckResultsFilter = {
    page: paginationModel.page,
    limit: paginationModel.pageSize,
    promoDisplayConfigId: query.get('promoDisplayConfigId') || '',
    region: query.get('region') || '',
    resultCode: query.get('resultCode') || '',
    displayOptionKey: query.get('displayOptionKey') || '',
  };

  const [regions, setRegions] = useState<ExtendedRegion[]>([]);

  const { result, isLoading, onSubmit } = usePromoDisplayCheckResults(
    deleteNulls(currentFilter, ['region', 'resultCode', 'promoDisplayConfigId']),
  );
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();
  const { tenant } = useCurrentTenant();

  useEffect(() => {
    setRegions(getRegions(tenant.brand));
  }, [tenant.brand]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
    const newFilter: GetPromoDisplayCheckResultsFilter = {
      ...currentFilter,
      page: newModel.page,
      limit: newModel.pageSize,
    };
    onSubmit(newFilter);
  };

  const handleFormSubmit = (formData: {
    promoDisplayConfigId: string;
    region: string;
    resultCode: string;
    displayOptionKey: string;
  }) => {
    const newFilter: GetPromoDisplayCheckResultsFilter = {
      page: 0, // Reset to first page when searching
      limit: paginationModel.pageSize,
      ...formData,
    };
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    onSubmit(newFilter);
  };

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom>
        Promo Display Check Results ({result?.total ?? '--'})
      </Typography>
      <Box minWidth={1000} sx={{ mb: 2 }}>
        <TextField
          {...register('promoDisplayConfigId')}
          label="Promo Display Config ID"
          type="text"
          error={!!errors.promoDisplayConfigId}
          helperText={errors.promoDisplayConfigId?.message}
          sx={{ mr: 2, mb: 2 }}
        />
        <FormControl sx={{ mr: 2, mb: 2, minWidth: 120 }}>
          <InputLabel>Region</InputLabel>
          <Select {...register('region')} label="Region">
            <MenuItem value="">All</MenuItem>
            {regions.map((region) => (
              <MenuItem key={region.code} value={region.code}>
                {region.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isPromoMetaLoading ? (
          <Skeleton variant="rectangular" height={50} />
        ) : (
          <FormControl sx={{ mr: 2, mb: 2, minWidth: 200 }}>
            <InputLabel>Result Code</InputLabel>
            <Select {...register('resultCode')} label="Result Code">
              <MenuItem value="">All</MenuItem>
              {promoMeta?.promo_display_config_result_code?.map((resultCode) => (
                <MenuItem key={resultCode.resultCode} value={resultCode.resultCode}>
                  {resultCode.resultCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField {...register('displayOptionKey')} label="Display Option Key" type="text" sx={{ mr: 2, mb: 2 }} />
        <Button onClick={handleSubmit(handleFormSubmit)} type="submit" variant="contained" color="primary">
          Search
        </Button>
      </Box>
      {isLoading || isPromoMetaLoading ? (
        <Skeleton variant="rectangular" height={500} />
      ) : (
        <DataGrid
          columns={getColumns(promoMeta?.promo_display_config_result_code || [])}
          rows={result?.promoDisplayCheckResults || []}
          rowCount={result?.total || 0}
          paginationMode="server"
          getRowId={(row) => `${row.promoDisplayConfigId}-${row.createdAt}`}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
        />
      )}
    </Container>
  );
}
